import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, MapPin, Navigation } from "lucide-react";
import { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSitePath } from "../context/SiteContext";
import { toast } from "sonner";
import { api, apiError, toCriteria } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { StepLayout } from "../components/StepLayout";
import { AmenityList } from "../components/Amenities";
import { RoomCard } from "../components/RoomCard";
import { Skeleton } from "../components/ui/skeleton";
import { useLocations } from "../components/SearchPanel";
import { SafeImg } from "../components/SafeImg";

export default function Rates() {
  const { hotelId } = useParams();
  const { criteria, hotel, rate, selectHotel, selectRate, setCriteria } = useBooking();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const arrival = params.get("arrival");
    if (["google", "google_ads"].includes(params.get("src"))) {
      const today = new Date().toISOString().slice(0, 10);
      setCriteria({ ...(arrival && arrival >= today ? { arrival } : {}),
        nights: Number(params.get("nights")) || 1, adults: Number(params.get("adults")) || 2, hotel_id: hotelId });
      setParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const navigate = useNavigate();
  const sp = useSitePath();
  const { data: loc } = useLocations();
  const backTo = loc?.hotels?.length === 1 ? "/" : "/hotels";
  const { data, isLoading, error } = useQuery({
    queryKey: ["rates", hotelId, criteria],
    queryFn: () => api.post(`/hotels/${hotelId}/rates`, toCriteria(criteria)).then((r) => r.data),
  });
  useEffect(() => {
    if (data && hotel?.id !== data.hotel.id) selectHotel(data.hotel);
  }, [data, hotel, selectHotel]);
  useEffect(() => {
    if (data?.promo && !data.promo.valid) toast.error(`Promotion code ${data.promo.code} isn't valid for these dates`);
  }, [data?.promo]);

  const actions = (
    <div className="flex gap-3">
      <button className="btn-outline flex-1" onClick={() => navigate(sp(backTo))} data-testid="rates-back-button"><ArrowLeft className="h-4 w-4" />Back</button>
      <button className="btn-primary flex-1 disabled:opacity-40" disabled={!rate} onClick={() => navigate(sp("/services"))} data-testid="rates-continue-button">Continue<ArrowRight className="h-4 w-4" /></button>
    </div>
  );
  const h = data?.hotel;
  return (
    <StepLayout step={2} sidebarActions={actions} mobileBar={{ disabled: !rate, onClick: () => navigate(sp("/services")) }}>
      {isLoading && <Skeleton className="h-80 rounded-2xl" />}
      {error && <p className="text-red-600" data-testid="rates-error">{apiError(error)}</p>}
      {h && (
        <>
          <section className="overflow-hidden rounded-3xl bg-white" data-testid="rates-hotel-header">
            {h.show_image !== false && <SafeImg src={h.image} alt={h.name} className="h-64 w-full object-cover sm:h-80" />}
            <div className="p-6 sm:p-8">
              <p className="eyebrow">{h.city}, {h.country}</p>
              <h1 className="mt-2 font-serif text-4xl text-ink sm:text-5xl">{h.name}</h1>
              {h.show_description !== false && h.description && <p className="mt-3 max-w-2xl text-ink/70" data-testid="rates-hotel-description">{h.description}</p>}
              <AmenityList features={h.features} className="mt-5" />
              <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-ink/60">
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-brand" />{h.address}</span>
                <a className="btn-ghost" target="_blank" rel="noreferrer" href={`https://maps.google.com/maps?saddr=&daddr=${encodeURIComponent(h.address)}`} data-testid="rates-get-directions">
                  <Navigation className="h-4 w-4" />Get directions
                </a>
              </div>
            </div>
          </section>
          {data.promo?.valid && <p className="mt-6 rounded-xl bg-gold/15 px-4 py-3 text-sm text-ink" data-testid="promo-applied">Promotion code <b className="font-mono">{data.promo.code}</b> applied{data.promo.discount > 0 ? `: up to ${Math.round(data.promo.discount * 100)}% off accommodation` : ". Special rates are shown below"}.</p>}
          {data.promo && !data.promo.valid && <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="promo-invalid">Promotion code <b className="font-mono">{data.promo.code}</b> isn't valid for these dates, so our standard rates are shown. Check the code or try other dates.</p>}
          <h2 className="mt-10 font-serif text-3xl text-ink">Rooms & rates</h2>
          <div className="mt-6 space-y-6" data-testid="rooms-list">
            {data.rooms.map((r) => (
              <RoomCard key={r.id} room={r} currency={data.currency} selectedRateId={rate && hotel?.id === h.id && rate.room_id === r.id ? rate.id : null}
                onSelect={(p) => selectRate({ id: r.id, name: r.name }, { id: p.id, name: p.name, room_id: r.id })} />
            ))}
          </div>
        </>
      )}
    </StepLayout>
  );
}
